import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useRef } from 'react';
import { InputField } from '../../../../../components/formFields/InputField';
import Label from '@/components/Label';
import { AreaField } from '../../../../../components/formFields/AreaField';
import { CheckboxField } from '../../../../../components/formFields/Checkbox';
import { useSelector } from 'react-redux';
import { useGetClassificationFilters } from '../../hooks/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/Select';

interface TypeMarkModalProps {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  isTemplate?: boolean;
  isMultiMode?: boolean;
}

const TypeMarkModal = ({
  visible,
  title,
  formProps,
  onSubmit,
  onClose,
  isTemplate,
  isMultiMode,
}: TypeMarkModalProps) => {
  const multiMode = Boolean(isMultiMode);

  const { menuId } = useSelector((state: any) => state.typeMark);
  const { data: classificationOptions = [], isLoading: classificationLoading } =
    useGetClassificationFilters(!isTemplate ? menuId : null);

  const ValidationSchema = Yup.object().shape({
    title: !multiMode && Yup.string().nullable().required('Povinné pole'),
    type_mark: !isTemplate && !multiMode && Yup.string().nullable().required('Povinné pole'),
    description: multiMode && Yup.string().nullable().required('Povinné pole'),
  });

  const formRef = useRef(null);

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    (formRef.current as any).submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title ?? null,
              type_mark: formProps?.type_mark ?? null,
              description: formProps?.description ?? null,
              is_active: formProps?.is_active ?? true,
              classification: formProps?.classification ?? null,
            }}
            onSubmit={(values) => {
              const { type_mark: _type_mark, title, ...valuesWithoutTypeMark } = values;

              onSubmit(
                isTemplate || multiMode
                  ? {
                      id: formProps?.id,
                      values: isTemplate
                        ? { title, ...valuesWithoutTypeMark }
                        : multiMode
                        ? { description: values.description }
                        : valuesWithoutTypeMark,
                    }
                  : { values }
              );
            }}
          >
            {(props) => {
              const { values, setFieldValue } = props;
              return (
                <>
                  <Form>
                    <div className="space-y-1">
                      {!multiMode && (
                        <>
                          <div>
                            <Label name="title" title="Název" required />
                            <Field
                              name="title"
                              value={values.title || null}
                              placeholder="Název"
                              component={InputField}
                              data-testid="typemark-modal-title-input"
                            />
                          </div>

                          {!isTemplate && (
                            <div>
                              <Label name="type_mark" title="Označení" required />
                              <Field
                                name="type_mark"
                                value={values.type_mark || null}
                                placeholder="Označení"
                                component={InputField}
                                data-testid="typemark-modal-type-mark-input"
                              />
                            </div>
                          )}
                        </>
                      )}

                      <div>
                        <Label name="description" title="Popis" required={multiMode} />
                        <Field
                          name="description"
                          value={values.description || null}
                          placeholder="Popis"
                          limit={600}
                          component={AreaField}
                          data-testid="typemark-modal-description-input"
                        />
                      </div>

                      {!isTemplate && !formProps && !multiMode && (
                        <div>
                          <Label name="classification" title="Zatřídění" required />
                          <Select
                            value={values.classification ?? ''}
                            onValueChange={(val) => setFieldValue('classification', val)}
                            disabled={classificationLoading}
                          >
                            <SelectTrigger
                              data-testid="typemark-modal-classification-select"
                              className="w-full"
                            >
                              <SelectValue placeholder="Vyberte klasifikaci..." />
                            </SelectTrigger>
                            <SelectContent position="popper" className="z-[10001]">
                              {classificationOptions.map((opt: any) => (
                                <SelectItem key={opt.id} value={opt.id}>
                                  {opt.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {!multiMode && (
                        <div className="flex gap-2 items-center">
                          <Label name="is_active" title="Aktvní" />
                          <Field
                            name="is_active"
                            value={values.is_active}
                            placeholder="Aktivní"
                            component={CheckboxField}
                            data-testid="typemark-modal-is-active-checkbox"
                          />
                        </div>
                      )}
                    </div>
                  </Form>
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            data-testid="typemark-modal-close-btn"
            key={1}
            variant="outline"
            onClick={() => onClose()}
          >
            Zavřít
          </Button>
          <Button data-testid="typemark-modal-submit-btn" key={2} onClick={() => onSubmitButton()}>
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TypeMarkModal;
